import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  BanknotesIcon,
  ChartBarIcon,
  BuildingLibraryIcon,
  ArrowRightIcon,
  LockClosedIcon,
  CheckCircleIcon,
  StarIcon,
  MapPinIcon,
  ClockIcon,
  UserPlusIcon,
  DocumentTextIcon,
  SparklesIcon,
} from '@heroicons/react/24/solid';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import ScrollToTop from '../components/common/ScrollToTop';

const testimonials = [
  {
    name: 'Keoni Nakamura',
    role: 'Small Business Owner, Kaimuki',
    content: 'Willowvale Credit Union helped me grow my business with their personalized approach and local focus.',
  },
  {
    name: 'Lani Foster',
    role: 'First-Time Homebuyer',
    content: 'The team made my dream of homeownership a reality with clear guidance and competitive rates.',
  },
  {
    name: 'Marcus Villanueva',
    role: 'Member since 2019',
    content: 'Their checking and savings options plus real support when I need it — that’s why I stay.',
  },
];

const services = [
  {
    name: 'Checking & Savings',
    description: 'Accounts designed for how you live and save.',
    features: ['Free checking', 'High-yield savings', 'Online & mobile banking'],
    icon: BuildingLibraryIcon,
  },
  {
    name: 'Loans & Credit',
    description: 'Rates and terms that work for you.',
    features: ['Auto loans', 'Personal loans', 'Credit cards'],
    icon: BanknotesIcon,
  },
  {
    name: 'Business Banking',
    description: 'Solutions for local businesses and sole proprietors.',
    features: ['Business checking', 'Merchant services', 'Commercial lending'],
    icon: ChartBarIcon,
  },
];

const trustItems = [
  { label: 'NCUA insured', sub: 'Up to $250,000' },
  { label: 'Member-owned', sub: 'Not for profit' },
  { label: 'Local', sub: 'Honolulu, HI' },
  { label: '24/7 access', sub: 'Online & mobile' },
];

const joinSteps = [
  {
    step: 1,
    icon: UserPlusIcon,
    title: 'Check eligibility',
    description: 'Live, work, or worship in Hawaii? You can join. We’ll confirm in seconds.',
  },
  {
    step: 2,
    icon: DocumentTextIcon,
    title: 'Apply online',
    description: 'Open your account in minutes with a valid ID and basic info. No branch visit required.',
  },
  {
    step: 3,
    icon: SparklesIcon,
    title: 'Start banking',
    description: 'Get your account details, set up online banking, and access your money right away.',
  },
];

const rateHighlights = [
  { label: 'Savings APY', value: '4.25%', sub: 'High-yield savings', icon: BuildingLibraryIcon },
  { label: 'Auto loans', value: '6.49%', sub: 'APR from', icon: BanknotesIcon },
  { label: 'Personal loans', value: '9.99%', sub: 'APR from', icon: ChartBarIcon },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero — bank-worthy, clear, trustworthy */}
      <section className="relative min-h-[85vh] flex flex-col justify-center bg-[#0a1628] overflow-hidden">
        {/* Subtle grid / grain for depth */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 text-center">
          <p className="text-primary-300 text-sm font-medium tracking-widest uppercase mb-6">
            Willowvale Credit Union
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6">
            Banking that puts
            <br />
            <span className="text-primary-300">members first.</span>
          </h1>
          <p className="text-slate-300 text-lg sm:text-xl max-w-2xl mx-auto mb-10">
            Member-owned, community-focused, and here for you in Hawaii. Better rates, fewer fees, and real people when you need them.
          </p>

          {/* Trust line */}
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-slate-400 text-sm mb-10">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheckIcon className="h-4 w-4 text-primary-400" />
              NCUA insured
            </span>
            <span>Member-owned</span>
            <span className="inline-flex items-center gap-1.5">
              <MapPinIcon className="h-4 w-4" />
              600 Kapiolani Blvd, Honolulu
            </span>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Open an account
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-all duration-200"
            >
              Log in
            </Link>
          </div>
        </div>
      </section>

      {/* Trust bar — compact strip */}
      <section className="bg-slate-50 border-y border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {trustItems.map((item) => (
              <div key={item.label}>
                <div className="text-slate-900 font-semibold">{item.label}</div>
                <div className="text-slate-500 text-sm mt-0.5">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Primary services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Accounts and services built for you
            </h2>
            <p className="text-slate-600 text-lg">
              From everyday checking to savings and loans, we’re here to help you manage your money with clarity and confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.name}
                className="group bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="p-3 bg-primary-100 rounded-xl w-fit mb-6">
                  <service.icon className="h-7 w-7 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{service.name}</h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-slate-700 text-sm">
                      <CheckCircleIcon className="h-5 w-5 mr-2 text-primary-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700"
            >
              View all products
              <ArrowRightIcon className="ml-1 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Rates at a glance */}
      <section className="py-20 lg:py-28 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary-400 text-sm font-semibold tracking-wider uppercase mb-3">Member rates</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Rates that work for you
            </h2>
            <p className="text-slate-400 text-lg">
              As a member-owned credit union, we pass savings back to you through competitive rates on savings and loans.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {rateHighlights.map((item) => (
              <div
                key={item.label}
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-primary-500/50 hover:bg-white/[0.08] transition-all duration-300"
              >
                <div className="p-2.5 bg-primary-500/20 rounded-xl w-fit mb-5">
                  <item.icon className="h-6 w-6 text-primary-400" />
                </div>
                <p className="text-slate-400 text-sm font-medium mb-1">{item.label}</p>
                <p className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{item.value}</p>
                <p className="text-slate-500 text-sm mt-1">{item.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            Rates shown are examples; actual rates depend on product and eligibility. See our{' '}
            <Link to="/products" className="text-primary-400 hover:text-primary-300 font-medium">products page</Link> for current rates.
          </p>
        </div>
      </section>

      {/* Why choose us — simple two-column or cards */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Why members choose Willowvale
              </h2>
              <p className="text-slate-600 text-lg mb-10">
                We’re a credit union — that means you’re a member, not just a customer. Better rates, fewer fees, and decisions made with our community in mind.
              </p>
              <ul className="space-y-4">
                {[
                  'Competitive rates on savings and loans',
                  'Free checking with no hidden fees',
                  'Local team you can call or visit',
                  'Secure digital banking 24/7',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircleIcon className="h-6 w-6 text-primary-500 shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-lg border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <LockClosedIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Online banking</h3>
                  <p className="text-slate-500 text-sm">Safe, simple, always available</p>
                </div>
              </div>
              <p className="text-slate-600 mb-6">
                Manage your accounts, pay bills, transfer money, and deposit checks from your phone or computer.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700"
              >
                Log in to online banking
                <ArrowRightIcon className="ml-1 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How to join — 3 steps */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Become a member in three steps
            </h2>
            <p className="text-slate-600 text-lg">
              Joining Willowvale is simple. No complicated paperwork — just a quick application and you’re in.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {joinSteps.map((item) => (
              <div key={item.step} className="relative">
                <div className="relative bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-lg shrink-0">
                      {item.step}
                    </span>
                    <div className="p-2.5 bg-white rounded-xl shadow-sm border border-slate-100">
                      <item.icon className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 flex-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Become a member
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Trusted by our community
            </h2>
            <p className="text-slate-600 text-lg">
              Hear from members who bank with Willowvale Credit Union.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.name}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-slate-500 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0a1628]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to bank with a credit union that puts you first?
          </h2>
          <p className="text-slate-300 text-lg mb-10">
            Open an account in minutes. No monthly fees on basic checking, and a local team ready to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
            >
              Open an account
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-colors"
            >
              Contact us
            </Link>
          </div>
          <p className="mt-8 text-slate-400 text-sm inline-flex items-center gap-2">
            <ClockIcon className="h-4 w-4" />
            Visit us at 600 Kapiolani Blvd, Honolulu, HI 96813
          </p>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
